import React, { useState, useEffect, useCallback } from 'react';
import {
    MdRefresh, MdCheckCircle, MdCancel, MdVisibility,
    MdClose, MdPeople, MdEmail, MdPhone,
    MdCalendarToday, MdDownload, MdAutorenew, MdPayments, MdBlock
} from 'react-icons/md';
import API from '../api';
import { useAuth } from '../hooks/useAuth';
import { translateUiText, useLanguage } from '../i18n';
import { useSiteSettings } from '../hooks/useSiteSettings';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const STATUS_META = {
    pending: { key: 'bookings.statusPending', fallback: 'Pending', bg: '#fef3c7', color: '#b45309' },
    confirmed: { key: 'bookings.statusConfirmed', fallback: 'Confirmed', bg: '#dcfce7', color: '#15803d' },
    cancelled: { key: 'bookings.statusCancelled', fallback: 'Cancelled', bg: '#fee2e2', color: '#b91c1c' },
    terminated: { key: 'bookings.statusTerminated', fallback: 'Terminated', bg: '#fce7f3', color: '#be185d' },
};

const PAYMENT_META = {
    unpaid: { key: 'bookings.paymentUnpaid', fallback: 'Unpaid', bg: '#fee2e2', color: '#b91c1c' },
    partial: { key: 'bookings.paymentPartial', fallback: 'Partial', bg: '#fef3c7', color: '#b45309' },
    paid: { key: 'bookings.paymentPaid', fallback: 'Paid', bg: '#dcfce7', color: '#15803d' },
};

function StatusBadge({ status }) {
    const { t } = useLanguage();
    const m = STATUS_META[status] || { key: null, fallback: status, bg: '#f1f5f9', color: '#475569' };
    const label = m.key ? t(m.key, m.fallback) : m.fallback;
    return <span style={{ fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 12, background: m.bg, color: m.color, whiteSpace: 'nowrap' }}>{label}</span>;
}

function PaymentBadge({ status }) {
    const { t } = useLanguage();
    const m = PAYMENT_META[status] || PAYMENT_META.unpaid;
    return <span style={{ fontSize: 11, fontWeight: 600, padding: '3px 8px', borderRadius: 12, background: m.bg, color: m.color, whiteSpace: 'nowrap' }}>{t(m.key, m.fallback)}</span>;
}

function Modal({ title, onClose, children }) {
    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-box" style={{ maxWidth: 560 }} onClick={e => e.stopPropagation()}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid #e2e8f0' }}>
                    <h6 style={{ margin: 0, fontWeight: 600 }}>{title}</h6>
                    <button onClick={onClose} style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#64748b' }}><MdClose size={20} /></button>
                </div>
                <div style={{ padding: 20 }}>{children}</div>
            </div>
        </div>
    );
}

function downloadBookingsPDF(bookings, facilityName, t) {
    const doc = new jsPDF();
    doc.setFontSize(18);
    doc.setTextColor(14, 165, 233);
    doc.text(t('bookings.pdfHeading', 'FMS Pro — Booking Requests'), 14, 18);
    doc.setFontSize(11);
    doc.setTextColor(100, 116, 139);
    doc.text(`${t('bookings.pdfFacilityLabel', 'Facility:')} ${facilityName || t('bookings.pdfAllFacilities', 'All')}`, 14, 26);
    doc.text(`${t('bookings.pdfGeneratedLabel', 'Generated:')} ${new Date().toLocaleString()}`, 14, 32);

    autoTable(doc, {
        startY: 40,
        head: [['#', t('bookings.pdfColGuest', 'Guest'), t('bookings.pdfColEmail', 'Email'), t('bookings.pdfColSpace', 'Space'), t('bookings.pdfColStart', 'Start'), t('bookings.pdfColEnd', 'End'), t('bookings.pdfColAttendees', 'Attendees'), t('bookings.pdfColStatus', 'Status')]],
        body: bookings.map((b, i) => [
            i + 1,
            `${b.guest_name || b.booked_by_name || '—'}${b.company ? `\n${b.company}` : ''}`,
            b.guest_email || '—',
            `${b.space_name}\n${b.building_name}`,
            b.start_datetime ? new Date(b.start_datetime).toLocaleString() : '—',
            b.end_datetime ? new Date(b.end_datetime).toLocaleString() : '—',
            b.attendees || 1,
            b.status,
        ]),
        styles: { fontSize: 8, cellPadding: 3 },
        headStyles: { fillColor: [14, 165, 233], textColor: 255, fontStyle: 'bold' },
        alternateRowStyles: { fillColor: [248, 250, 252] },
        columnStyles: { 2: { cellWidth: 36 }, 3: { cellWidth: 32 } },
    });

    doc.save(`Bookings_${facilityName || 'All'}_${new Date().toISOString().split('T')[0]}.pdf`);
}

export default function Bookings() {
    const { assignedFacility } = useAuth();
    const { currency } = useSiteSettings();
    const { t } = useLanguage();
    const [bookings, setBookings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filterStatus, setFilterStatus] = useState('');
    const [selected, setSelected] = useState(null);
    const [saving, setSaving] = useState(false);
    const [paymentAmount, setPaymentAmount] = useState('');
    const [renewStart, setRenewStart] = useState('');
    const [renewEnd, setRenewEnd] = useState('');
    const [terminationReason, setTerminationReason] = useState('');

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = filterStatus ? { status: filterStatus } : {};
            const { data } = await API.get('/spaces/bookings', { params });
            setBookings(data);
        } finally { setLoading(false); }
    }, [filterStatus]);

    useEffect(() => { load(); }, [load]);

    const updateStatus = async (id, status) => {
        setSaving(true);
        try {
            await API.put(`/spaces/bookings/${id}`, { status });
            setSelected(null);
            load();
        } finally { setSaving(false); }
    };

    const openBooking = booking => {
        setSelected(booking);
        setPaymentAmount(booking.amount_paid || 0);
        setRenewStart(''); setRenewEnd(''); setTerminationReason('');
    };

    const updatePayment = async () => {
        setSaving(true);
        try { await API.put(`/spaces/bookings/${selected.id}`, { amount_paid: paymentAmount }); setSelected(null); await load(); }
        catch (err) { alert(err.response?.data?.error || translateUiText('Payment update failed')); }
        finally { setSaving(false); }
    };

    const renewBooking = async () => {
        if (!renewStart || !renewEnd) return alert(translateUiText('Choose the renewal start and end times.'));
        setSaving(true);
        try { await API.post(`/spaces/bookings/${selected.id}/renew`, { start_datetime: renewStart, end_datetime: renewEnd }); setSelected(null); await load(); }
        catch (err) { alert(err.response?.data?.error || translateUiText('Renewal failed')); }
        finally { setSaving(false); }
    };

    const terminateBooking = async () => {
        if (!terminationReason.trim()) return alert(translateUiText('Enter a termination reason.'));
        if (!window.confirm(translateUiText('Terminate this booking? This action cannot be undone.'))) return;
        setSaving(true);
        try { await API.put(`/spaces/bookings/${selected.id}`, { terminate: true, termination_reason: terminationReason }); setSelected(null); await load(); }
        catch (err) { alert(err.response?.data?.error || translateUiText('Termination failed')); }
        finally { setSaving(false); }
    };

    const deleteBooking = async booking => {
        const guestName = booking.guest_name || booking.booked_by_name || translateUiText('this guest');
        if (!window.confirm(`${translateUiText('Delete booking request for')} ${guestName}?`)) return;
        setSaving(true);
        try {
            await API.delete(`/spaces/bookings/${booking.id}`);
            setSelected(null);
            load();
        } catch (err) {
            alert(err.response?.data?.error || translateUiText('Delete failed'));
        } finally { setSaving(false); }
    };

    const pendingCount = bookings.filter(b => b.status === 'pending').length;

    const counts = {
        all: bookings.length,
        pending: bookings.filter(b => b.status === 'pending').length,
        confirmed: bookings.filter(b => b.status === 'confirmed').length,
        cancelled: bookings.filter(b => b.status === 'cancelled').length,
    };

    return (
        <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
                <div>
                    <h4 style={{ margin: 0, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 10 }}>
                        {t('bookings.pageTitle', 'Booking Requests')}
                        {pendingCount > 0 && (
                            <span style={{ background: '#f59e0b', color: '#fff', fontSize: 12, fontWeight: 700, padding: '2px 8px', borderRadius: 12 }}>
                                {pendingCount} {t('bookings.pendingSuffix', 'pending')}
                            </span>
                        )}
                    </h4>
                    <p style={{ margin: 0, color: '#64748b', fontSize: 13 }}>
                        {assignedFacility?.name || t('bookings.allFacilities', 'All facilities')} {t('bookings.subtitle', '— manage space booking requests')}
                    </p>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                    <button className="btn btn-light btn-sm" onClick={load}
                        style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <MdRefresh size={16} /> {t('bookings.refresh', 'Refresh')}
                    </button>
                    <button className="btn btn-primary btn-sm" disabled={bookings.length === 0}
                        onClick={() => downloadBookingsPDF(bookings, assignedFacility?.name, t)}
                        style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <MdDownload size={16} /> {t('bookings.downloadPdf', 'Download PDF')}
                    </button>
                </div>
            </div>

            {/* Stats */}
            <div className="row g-3 mb-4">
                {[
                    { label: t('bookings.statTotal', 'Total'), value: counts.all, cls: 'blue', icon: <MdCalendarToday /> },
                    { label: t('bookings.statPending', 'Pending'), value: counts.pending, cls: 'yellow', icon: <MdCalendarToday /> },
                    { label: t('bookings.statConfirmed', 'Confirmed'), value: counts.confirmed, cls: 'green', icon: <MdCheckCircle /> },
                    { label: t('bookings.statCancelled', 'Cancelled'), value: counts.cancelled, cls: 'red', icon: <MdCancel /> },
                ].map(s => (
                    <div key={s.label} className="col-6 col-md-3">
                        <div className="stat-card">
                            <div className={`stat-icon ${s.cls}`}>{s.icon}</div>
                            <div><div className="stat-value">{s.value}</div><div className="stat-label">{s.label}</div></div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Filter tabs */}
            <div style={{ display: 'flex', gap: 0, marginBottom: 16, borderBottom: '1px solid #e2e8f0', flexWrap: 'wrap' }}>
                {[
                    { value: '', label: `${t('bookings.tabAll', 'All')} (${counts.all})` },
                    { value: 'pending', label: `${t('bookings.tabPending', 'Pending')} (${counts.pending})` },
                    { value: 'confirmed', label: `${t('bookings.tabConfirmed', 'Confirmed')} (${counts.confirmed})` },
                    { value: 'cancelled', label: `${t('bookings.tabCancelled', 'Cancelled')} (${counts.cancelled})` },
                ].map(tab => (
                    <button key={tab.value} onClick={() => setFilterStatus(tab.value)} style={{
                        padding: '9px 16px', background: 'none', border: 'none', cursor: 'pointer',
                        fontWeight: 500, fontSize: 13,
                        color: filterStatus === tab.value ? '#0ea5e9' : '#64748b',
                        borderBottom: filterStatus === tab.value ? '2px solid #0ea5e9' : '2px solid transparent',
                    }}>{tab.label}</button>
                ))}
            </div>

            {/* Table */}
            <div className="page-card">
                <div style={{ overflowX: 'auto' }}>
                    {loading ? (
                        <div className="spinner-wrap"><div className="spinner-border" /></div>
                    ) : (
                        <table className="fms-table">
                            <thead>
                                <tr>
                                    <th>{t('bookings.colGuest', 'Guest')}</th><th>{t('bookings.colSpace', 'Space')}</th><th>{t('bookings.colWhen', 'When')}</th>
                                    <th style={{ width: 80 }}>{t('bookings.colGuestsCount', 'Guests')}</th><th>{t('bookings.colPurpose', 'Purpose')}</th>
                                    <th style={{ width: 100 }}>{t('bookings.colStatus', 'Status')}</th><th style={{ width: 115 }}>{t('bookings.colPayment', 'Payment')}</th>
                                    <th style={{ width: 50 }}>{t('bookings.colPublic', 'Public')}</th>
                                    <th style={{ width: 130 }}>{t('bookings.colActions', 'Actions')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {bookings.map(b => (
                                    <tr key={b.id} style={{ fontWeight: b.status === 'pending' ? 600 : 'normal' }}>
                                        <td>
                                            <div style={{ fontSize: 13, fontWeight: 600 }}>{b.guest_name || b.booked_by_name || '—'}</div>
                                            {b.guest_email && <div style={{ fontSize: 11, color: '#94a3b8' }}>{b.guest_email}</div>}
                                            {b.company && <div style={{ fontSize: 11, color: '#0369a1' }}>{b.company}</div>}
                                        </td>
                                        <td>
                                            <div style={{ fontSize: 13, fontWeight: 500 }}>{b.space_name}</div>
                                            <div style={{ fontSize: 11, color: '#94a3b8' }}>{b.building_name}</div>
                                        </td>
                                        <td style={{ fontSize: 12 }}>
                                            <div>{b.start_datetime ? new Date(b.start_datetime).toLocaleDateString() : '—'}</div>
                                            <div style={{ color: '#64748b' }}>
                                                {b.start_datetime ? new Date(b.start_datetime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}
                                                {b.end_datetime ? ` – ${new Date(b.end_datetime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}` : ''}
                                            </div>
                                        </td>
                                        <td style={{ fontSize: 13, textAlign: 'center' }}>{b.attendees || 1}</td>
                                        <td style={{ fontSize: 12, color: '#64748b', maxWidth: 160 }}>
                                            <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                {b.title || b.purpose || '—'}
                                            </div>
                                        </td>
                                        <td><StatusBadge status={b.terminated_at ? 'terminated' : b.status} /></td>
                                        <td><PaymentBadge status={b.payment_status} /><div style={{ fontSize: 11, color: '#64748b' }}>{b.currency || currency} {Number(b.amount_paid || 0).toLocaleString()} / {Number(b.total_amount || 0).toLocaleString()}</div></td>
                                        <td style={{ textAlign: 'center' }}>
                                            {b.is_public
                                                ? <span style={{ fontSize: 11, background: '#f0f9ff', color: '#0369a1', padding: '2px 6px', borderRadius: 8 }}>{t('bookings.badgePublic', 'Public')}</span>
                                                : <span style={{ fontSize: 11, color: '#94a3b8' }}>{t('bookings.badgeInternal', 'Internal')}</span>}
                                        </td>
                                        <td>
                                            <div style={{ display: 'flex', gap: 4 }}>
                                                <button className="btn btn-sm btn-light" title={t('bookings.actionViewDetailsTitle', 'View details')}
                                                    onClick={() => openBooking(b)}>
                                                    <MdVisibility size={14} />
                                                </button>
                                                {b.status === 'pending' && (
                                                    <>
                                                        <button className="btn btn-sm" title={t('bookings.actionConfirm', 'Confirm')}
                                                            onClick={() => updateStatus(b.id, 'confirmed')}
                                                            style={{ background: '#22c55e', color: '#fff', border: 'none', borderRadius: 6, padding: '3px 8px', fontSize: 11 }}>
                                                            ✓ {t('bookings.actionConfirm', 'Confirm')}
                                                        </button>
                                                        <button className="btn btn-sm btn-light" title={t('bookings.actionDeclineTitle', 'Decline')}
                                                            onClick={() => updateStatus(b.id, 'cancelled')}
                                                            style={{ color: '#ef4444', fontSize: 11 }}>
                                                            ✕
                                                        </button>
                                                    </>
                                                )}
                                                {b.status === 'confirmed' && (
                                                    <button className="btn btn-sm btn-light" onClick={() => updateStatus(b.id, 'cancelled')}
                                                        style={{ color: '#ef4444', fontSize: 11 }}>
                                                        {t('bookings.actionCancel', 'Cancel')}
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                                {!bookings.length && (
                                    <tr><td colSpan={9} style={{ textAlign: 'center', padding: 50, color: '#94a3b8' }}>
                                        <MdCalendarToday size={36} style={{ marginBottom: 8, display: 'block', margin: '0 auto 8px', color: '#cbd5e1' }} />
                                        {t('bookings.emptyState', 'No booking requests yet')}
                                    </td></tr>
                                )}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>

            {/* Detail modal */}
            {selected && (
                <Modal title={t('bookings.modalDetailsTitle', 'Booking Request Details')} onClose={() => setSelected(null)}>
                    <div className="row g-3" style={{ fontSize: 13 }}>
                        {/* Guest info */}
                        <div className="col-12">
                            <div style={{ background: '#f8fafc', borderRadius: 10, padding: '14px 16px' }}>
                                <div style={{ fontWeight: 600, marginBottom: 10, color: '#374151' }}>{t('bookings.sectionGuestInfo', '👤 Guest Information')}</div>
                                <div className="row g-2">
                                    <div className="col-6">
                                        <span style={{ color: '#94a3b8', fontSize: 12 }}>{t('bookings.labelName', 'Name')}</span><br />
                                        <strong>{selected.guest_name || selected.booked_by_name || '—'}</strong>
                                    </div>
                                    {selected.company && (
                                        <div className="col-6">
                                            <span style={{ color: '#94a3b8', fontSize: 12 }}>{t('bookings.labelCompany', 'Company')}</span><br />
                                            <strong>{selected.company}</strong>
                                        </div>
                                    )}
                                    {selected.guest_email && (
                                        <div className="col-6">
                                            <span style={{ color: '#94a3b8', fontSize: 12 }}><MdEmail size={12} /> {t('bookings.labelEmail', 'Email')}</span><br />
                                            <a href={`mailto:${selected.guest_email}`} style={{ color: '#0ea5e9' }}>{selected.guest_email}</a>
                                        </div>
                                    )}
                                    {selected.guest_phone && (
                                        <div className="col-6">
                                            <span style={{ color: '#94a3b8', fontSize: 12 }}><MdPhone size={12} /> {t('bookings.labelPhone', 'Phone')}</span><br />
                                            <strong>{selected.guest_phone}</strong>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Booking info */}
                        <div className="col-12">
                            <div style={{ background: '#f8fafc', borderRadius: 10, padding: '14px 16px' }}>
                                <div style={{ fontWeight: 600, marginBottom: 10, color: '#374151' }}>{t('bookings.sectionBookingDetails', '📅 Booking Details')}</div>
                                <div className="row g-2">
                                    <div className="col-6">
                                        <span style={{ color: '#94a3b8', fontSize: 12 }}>{t('bookings.labelSpace', 'Space')}</span><br />
                                        <strong>{selected.space_name}</strong>
                                        <div style={{ fontSize: 11, color: '#94a3b8' }}>{selected.building_name}</div>
                                    </div>
                                    <div className="col-6">
                                        <span style={{ color: '#94a3b8', fontSize: 12 }}>{t('bookings.labelAttendees', 'Attendees')}</span><br />
                                        <strong><MdPeople size={13} style={{ marginRight: 3 }} />{selected.attendees || 1}</strong>
                                    </div>
                                    <div className="col-6">
                                        <span style={{ color: '#94a3b8', fontSize: 12 }}>{t('bookings.labelFrom', 'From')}</span><br />
                                        <strong>{selected.start_datetime ? new Date(selected.start_datetime).toLocaleString() : '—'}</strong>
                                    </div>
                                    <div className="col-6">
                                        <span style={{ color: '#94a3b8', fontSize: 12 }}>{t('bookings.labelTo', 'To')}</span><br />
                                        <strong>{selected.end_datetime ? new Date(selected.end_datetime).toLocaleString() : '—'}</strong>
                                    </div>
                                    {(selected.title || selected.purpose) && (
                                        <div className="col-12">
                                            <span style={{ color: '#94a3b8', fontSize: 12 }}>{t('bookings.labelPurpose', 'Purpose')}</span><br />
                                            <span>{selected.title || selected.purpose}</span>
                                        </div>
                                    )}
                                    {selected.notes && (
                                        <div className="col-12">
                                            <span style={{ color: '#94a3b8', fontSize: 12 }}>{t('bookings.labelNotes', 'Notes')}</span><br />
                                            <span>{selected.notes}</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="col-6">
                            <span style={{ color: '#94a3b8', fontSize: 12 }}>{t('bookings.labelStatus', 'Status')}</span><br />
                            <StatusBadge status={selected.terminated_at ? 'terminated' : selected.status} />
                        </div>
                        <div className="col-6">
                            <span style={{ color: '#94a3b8', fontSize: 12 }}>{t('bookings.labelSource', 'Source')}</span><br />
                            <span style={{ fontSize: 12 }}>{selected.is_public ? t('bookings.sourcePublic', '🌐 Public Booking') : t('bookings.sourceInternal', '🏢 Internal')}</span>
                        </div>
                        {selected.termination_reason && <div className="col-12"><span style={{ color: '#94a3b8', fontSize: 12 }}>{t('bookings.labelTerminationReason', 'Termination reason')}</span><br /><span>{selected.termination_reason}</span></div>}
                        <div className="col-12">
                            <div style={{ background: '#f0fdf4', borderRadius: 10, padding: '14px 16px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                                    <strong><MdPayments size={16} /> {t('bookings.labelPayment', 'Payment')}</strong><PaymentBadge status={selected.payment_status} />
                                </div>
                                <div style={{ fontSize: 12, color: '#64748b', marginBottom: 8 }}>
                                    {selected.currency || currency} {Number(selected.amount_paid || 0).toLocaleString()} {t('bookings.paidOfText', 'paid of')} {selected.currency || currency} {Number(selected.total_amount || 0).toLocaleString()}
                                </div>
                                <div style={{ display: 'flex', gap: 8 }}>
                                    <input type="number" min="0" max={selected.total_amount || 0} step="0.01" className="form-control form-control-sm" value={paymentAmount} onChange={e => setPaymentAmount(e.target.value)} placeholder={t('bookings.amountReceivedPlaceholder', 'Amount received')} />
                                    <button className="btn btn-sm btn-success" disabled={saving} onClick={updatePayment}>{t('bookings.btnSavePayment', 'Save payment')}</button>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Actions */}
                    {selected.status === 'pending' && (
                        <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
                            <button className="btn btn-light flex-fill" onClick={() => setSelected(null)}>{t('bookings.btnClose', 'Close')}</button>
                            <button className="btn btn-light flex-fill" disabled={saving}
                                onClick={() => updateStatus(selected.id, 'cancelled')}
                                style={{ color: '#ef4444', borderColor: '#ef4444' }}>
                                {t('bookings.btnDecline', '✕ Decline')}
                            </button>
                            <button className="btn btn-primary flex-fill" disabled={saving}
                                onClick={() => updateStatus(selected.id, 'confirmed')}
                                style={{ background: '#22c55e', borderColor: '#22c55e' }}>
                                {saving ? <span className="spinner-border spinner-border-sm" /> : t('bookings.btnConfirmBooking', '✓ Confirm Booking')}
                            </button>
                            <button className="btn btn-light" disabled={saving} onClick={() => deleteBooking(selected)} style={{ color: '#ef4444', borderColor: '#ef4444' }} title={t('bookings.btnDelete', 'Delete')}>
                                {t('bookings.btnDelete', 'Delete')}
                            </button>
                        </div>
                    )}
                    {selected.status !== 'pending' && (
                        <div style={{ marginTop: 16 }}>
                            {selected.status === 'confirmed' && !selected.terminated_at && <div style={{ background: '#f8fafc', borderRadius: 10, padding: 14, marginBottom: 12 }}>
                                <strong style={{ fontSize: 13 }}><MdAutorenew size={16} /> {t('bookings.labelRenewBooking', 'Renew booking')}</strong>
                                <div className="row g-2 mt-1"><div className="col-6"><input type="datetime-local" className="form-control form-control-sm" value={renewStart} onChange={e => setRenewStart(e.target.value)} /></div><div className="col-6"><input type="datetime-local" className="form-control form-control-sm" value={renewEnd} onChange={e => setRenewEnd(e.target.value)} /></div></div>
                                <button className="btn btn-sm btn-outline-primary mt-2" disabled={saving} onClick={renewBooking}>{t('bookings.btnRequestRenewal', 'Request renewal')}</button>
                            </div>}
                            {selected.status === 'confirmed' && !selected.terminated_at && <div style={{ background: '#fff7ed', borderRadius: 10, padding: 14, marginBottom: 12 }}>
                                <strong style={{ fontSize: 13 }}><MdBlock size={16} /> {t('bookings.labelTerminateBooking', 'Terminate booking')}</strong>
                                <input className="form-control form-control-sm mt-2" value={terminationReason} onChange={e => setTerminationReason(e.target.value)} placeholder={t('bookings.terminationReasonPlaceholder', 'Reason for termination')} />
                                <button className="btn btn-sm btn-outline-danger mt-2" disabled={saving} onClick={terminateBooking}>{t('bookings.btnTerminate', 'Terminate')}</button>
                            </div>}
                            <div style={{ display: 'flex', gap: 10 }}>
                                <button className="btn btn-light flex-fill" onClick={() => setSelected(null)}>{t('bookings.btnClose', 'Close')}</button>
                                <button className="btn btn-light" disabled={saving} onClick={() => deleteBooking(selected)} style={{ color: '#ef4444', borderColor: '#ef4444' }}>{t('bookings.btnDelete', 'Delete')}</button>
                            </div>
                        </div>
                    )}
                </Modal>
            )}
        </div>
    );
}
